import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight, CornerDownLeft } from "lucide-react";
import { buildSearchIndex, searchIndex } from "@/utils/searchIndex";

/**
 * GlobalSearch — barra di ricerca globale con dropdown suggerimenti.
 *
 * Props:
 *  - variant: "hero" (bianca su sfondo blu) | "plain" (default, adatta a sfondi chiari)
 *  - placeholder: testo placeholder personalizzato
 *  - autoFocus: focus iniziale
 *  - onQueryChange: callback opzionale ricevuto ad ogni digitazione (usato ad esempio per
 *    filtrare inline la pagina Convenzioni)
 *  - resultsLimit: numero massimo risultati mostrati (default 8)
 *  - testId: data-testid del wrapper
 */
export default function GlobalSearch({
  variant = "plain",
  placeholder = "Cerca servizi, corsi, convenzioni, associazioni…",
  autoFocus = false,
  onQueryChange,
  onNavigate,
  resultsLimit = 8,
  testId = "global-search",
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // L'indice viene costruito una sola volta.
  const index = useMemo(() => buildSearchIndex(), []);

  const results = useMemo(() => searchIndex(index, query, resultsLimit), [index, query, resultsLimit]);

  // Notifica il parent (per il filtro inline)
  useEffect(() => {
    if (onQueryChange) onQueryChange(query);
  }, [query, onQueryChange]);

  // Reset highlight quando cambiano i risultati
  useEffect(() => {
    setHighlight(0);
  }, [results.length]);

  // Chiudi il dropdown al click fuori
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const go = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    setQuery("");
    navigate(item.path);
    if (onNavigate) onNavigate();
  }, [navigate, onNavigate]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(results.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      if (results[highlight]) {
        e.preventDefault();
        go(results[highlight]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  // Raggruppa i risultati per gruppo mantenendo l'ordine di score
  const grouped = useMemo(() => {
    const map = new Map();
    results.forEach((r, i) => {
      const arr = map.get(r.group) || [];
      arr.push({ ...r, _index: i });
      map.set(r.group, arr);
    });
    return Array.from(map.entries());
  }, [results]);

  const isHero = variant === "hero";
  const isTopbar = variant === "topbar";
  const isCompact = variant === "compact";

  const inputWrap = isHero
    ? "bg-white/95 border border-white/60 focus-within:border-white shadow-2xl rounded-xl"
    : isTopbar
      ? "bg-white/15 hover:bg-white/25 border border-white/25 focus-within:bg-white/25 focus-within:border-white/60 rounded-lg transition-all"
      : isCompact
        ? "bg-white border border-[var(--confcom-border)] focus-within:border-[var(--confcom-blue)] rounded-lg transition-all"
        : "bg-white border border-[var(--confcom-border)] focus-within:border-[var(--confcom-blue)] shadow-sm rounded-xl";

  const inputEl = isTopbar
    ? "flex-1 bg-transparent px-2 py-1 text-[13px] text-white placeholder:text-white/70 focus:outline-none caret-white"
    : isCompact
      ? "flex-1 bg-transparent px-2 py-1.5 text-[13px] text-[var(--confcom-text-primary)] placeholder:text-[var(--confcom-text-muted)] focus:outline-none"
      : "flex-1 bg-transparent px-3 py-3.5 text-[15px] text-[var(--confcom-text-primary)] placeholder:text-[var(--confcom-text-muted)] focus:outline-none";

  const iconSize = isTopbar || isCompact ? 14 : 20;
  const iconClass = isTopbar
    ? "text-white/90"
    : "text-[var(--confcom-blue)]";

  return (
    <div
      ref={wrapperRef}
      className="relative w-full"
      data-testid={testId}
    >
      <div className={`group flex items-center ${inputWrap}`}>
        <div className={isTopbar || isCompact ? "pl-3 shrink-0" : "pl-4 shrink-0"}>
          <Search size={iconSize} className={iconClass} aria-hidden="true" />
        </div>
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className={inputEl}
          aria-label="Cerca nel sito"
          aria-autocomplete="list"
          aria-expanded={open && results.length > 0}
          aria-controls={`${testId}-listbox`}
          data-testid={`${testId}-input`}
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(""); inputRef.current?.focus(); }}
            className="mr-1.5 p-2 rounded-full hover:bg-[var(--confcom-bg-subtle,#F5F7FA)] text-[var(--confcom-text-muted)]"
            aria-label="Cancella ricerca"
            data-testid={`${testId}-clear`}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {open && query.trim() && (
        <div
          id={`${testId}-listbox`}
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+8px)] z-[80] max-h-[70vh] overflow-y-auto rounded-xl border border-[var(--confcom-border)] bg-white shadow-2xl"
          data-testid={`${testId}-dropdown`}
        >
          {results.length === 0 ? (
            <div className="px-5 py-6 text-sm text-[var(--confcom-text-muted)]" data-testid={`${testId}-empty`}>
              Nessun risultato per <strong className="text-[var(--confcom-text-primary)]">&quot;{query}&quot;</strong>. Prova con altre parole (es. &quot;corsi&quot;, &quot;sicurezza&quot;, &quot;welfare&quot;…).
            </div>
          ) : (
            <>
              {grouped.map(([group, list]) => (
                <div key={group} className="py-2 border-b border-[var(--confcom-border)] last:border-b-0">
                  <div className="px-5 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-[var(--confcom-text-muted)]">
                    {group}
                  </div>
                  {list.map((item) => {
                    const isHl = item._index === highlight;
                    return (
                      <button
                        key={`${group}-${item.path}-${item._index}`}
                        type="button"
                        role="option"
                        aria-selected={isHl}
                        onMouseEnter={() => setHighlight(item._index)}
                        onClick={() => go(item)}
                        className={`w-full text-left px-5 py-2.5 flex items-start gap-3 transition-colors ${
                          isHl ? "bg-[#EBF5FF]" : "hover:bg-[#F5F8FC]"
                        }`}
                        data-testid={`${testId}-item-${item._index}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-[var(--confcom-text-primary)] truncate">{item.title}</div>
                          {item.subtitle && (
                            <div className="text-xs text-[var(--confcom-text-muted)] truncate mt-0.5">{item.subtitle}</div>
                          )}
                        </div>
                        <ArrowRight size={14} className={`shrink-0 mt-1 ${isHl ? "text-[var(--confcom-blue)]" : "text-[var(--confcom-text-muted)]"}`} aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
              ))}
              <div className="px-5 py-2.5 text-[11px] text-[var(--confcom-text-muted)] bg-[#FAFBFC] flex items-center gap-3 border-t border-[var(--confcom-border)]">
                <span className="inline-flex items-center gap-1"><CornerDownLeft size={12} /> Invio</span>
                <span>per aprire · ↑↓ per navigare · Esc per chiudere</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
